import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, FormControlLabel, Popover, Skeleton, Typography, useTheme } from '@mui/material'
import * as d3 from 'd3'

const defaultOptions = {
    showLegend: true,
    showSeriesVisibilityToggleOptions: false,
    showDownloadButton: true,
    containerStyles: {}
}

const GraphSkeleton = () => {
    const { layout, border, palette, shadows } = useTheme()

    return (
        <Box display="flex" padding={layout.pagePadding} border={border[1]} boxShadow={shadows[1]} bgcolor={palette.common.bg} borderRadius="10px" minHeight="350px" flexGrow={1} height="100%" flexDirection="column" gap="10px" width="100%">
            {/* Title Skeleton */}
            <Box display="flex" alignItems="flex-end" justifyContent="space-between">
                <Skeleton
                    variant="text"
                    width="40%"
                    height={30}
                    sx={{ mb: 1 }}
                />
                <Skeleton
                    variant="text"
                    width="30%"
                    height={20}
                    sx={{ mb: 1 }}
                />
            </Box>

            {/* Main Graph Area Skeleton */}
            <Skeleton
                variant="rectangular"
                width="100%"
                height={250}
                sx={{
                    borderRadius: layout?.borderRadius || '8px',
                    // Optional: subtle animation speed adjustment
                    animationDuration: "1.5s"
                }}
            />
            <Box display="flex" gap="10px" alignItems="flex-end" justifyContent="center">
                <Skeleton
                    variant="text"
                    width="20%"
                    height={20}
                    sx={{ mb: "10px" }}
                />
                <Skeleton
                    variant="text"
                    width="20%"
                    height={20}
                    sx={{ mb: "10px" }}
                />
            </Box>
        </Box>
    )
}

// --- PLOTTER COMPONENT ---
const MultiBarPlotter = ( {
    data,
    options,
    activeSeries,
    xAxisLabel,
    yAxisLabel,
    xAxisKey,
    width,
    height,
    showDetailsDialog,
    hideDetailsDialog,
    marginTop = 40,
    marginRight = 20,
    marginBottom = 50,
    marginLeft = 50
} ) => {
    const gx = useRef()
    const gy = useRef()

    const { x0, x1, y } = useMemo( () => {
        const scale0 = d3.scaleBand()
            .domain( data.map( d => d[xAxisKey] ) )
            .range( [marginLeft, width - marginRight] )
            .paddingInner( 0.2 )

        const scale1 = d3.scaleBand()
            .domain( activeSeries.map( s => s.key ) )
            .range( [0, scale0.bandwidth()] )
            .padding( 0.05 )

        const yScale = d3.scaleLinear()
            .domain( [0, Math.max( d3.max( data, d => d3.max( activeSeries, s => +d[s.key] ) ) * 1.1 || 5, 5 )] )
            .range( [height - marginBottom, marginTop] )

        return { x0: scale0, x1: scale1, y: yScale }
    }, [data, activeSeries, width, height, xAxisKey, marginTop, marginRight, marginBottom, marginLeft] )

    // Update Axes
    useEffect( () => {
        const xAxis = d3.select( gx.current ).call( d3.axisBottom( x0 ) )

        // 2. Select the text labels and rotate them
        xAxis.selectAll( "text" )
            .style( "text-anchor", "end" ) // Anchors text so it rotates from the end point
            .attr( "dx", "-.8em" )         // Slight horizontal offset
            .attr( "dy", ".15em" )         // Slight vertical offset
            .attr( "transform", "rotate(-65)" )

        d3.select( gy.current ).call( d3.axisLeft( y ).ticks( 5 ) )
    }, [x0, y] )

    return (
        <svg className='graph-svg-container'>

            {options.showLabels && <text className='graph-axis-label' textAnchor='middle' x={width / 2} y={height - 10}>{xAxisLabel}</text>}
            {options.showLabels && <text className='graph-axis-label' textAnchor='middle' transform='rotate(-90)' x={-height / 2} y={15}>{yAxisLabel}</text>}

            <g ref={gx} transform={`translate(0,${height - marginBottom})`} />
            <g ref={gy} transform={`translate(${marginLeft},0)`} />

            {data.map( ( d ) => (
                <g key={d[xAxisKey]} transform={`translate(${x0( d[xAxisKey] )},0)`}>
                    {activeSeries.map( s => (
                        <rect
                            key={s.key}
                            x={x1( s.key )}
                            y={y( d[s.key] )}
                            width={x1.bandwidth()}
                            height={Math.max( 0, height - marginBottom - y( d[s.key] ) )}
                            fill={s.color}
                            onMouseMove={( e ) => showDetailsDialog( e, d, s )}
                            onMouseLeave={hideDetailsDialog}
                            style={{ transition: 'all 0.3s ease' }}
                        />
                    ) )}
                </g>
            ) )}
        </svg>
    )
}

// --- MAIN LAYOUT COMPONENT ---
const MultiBarGraph = ( { loading = false, title, data, seriesConfig, xAxisKey = 'date', options = {}, xAxisLabel, yAxisLabel } ) => {


    const containerRef = useRef()
    const [dimensions, setDimensions] = useState( null )
    const [selectedData, setSelectedData] = useState( null )
    const [anchorPos, setAnchorPos] = useState( { left: 0, top: 0 } )
    const [visibility, setVisibility] = useState(
        seriesConfig.reduce( ( acc, s ) => ( { ...acc, [s.key]: true } ), {} )
    )

    const finalOptions = { ...defaultOptions, ...options }

    const { palette, border, shadows, layout } = useTheme()
    const activeSeries = seriesConfig.filter( s => visibility[s.key] )

    const handleResize = () => {
        if ( containerRef.current ) {
            const { width, height } = containerRef.current.getBoundingClientRect()
            setDimensions( { width, height } )
        }
    }

    useEffect( () => {
        handleResize()
        window.addEventListener( 'resize', handleResize )
        return () => window.removeEventListener( 'resize', handleResize )
    }, [] )

    const showDetailsDialog = ( e, d, s ) => {
        setSelectedData( { ...d, _activeLabel: s.label, _activeVal: d[s.key] } )
        setAnchorPos( { left: e.pageX, top: e.pageY } )
    }


    if ( loading )
        return <GraphSkeleton />


    return (
        <Box border={border[1]} boxShadow={shadows[1]} sx={finalOptions.containerStyles || {}} bgcolor={palette.common.bg} borderRadius="10px" minHeight="350px" flexGrow={1} height="100%" flexDirection="column" display="flex">
            <Box padding={layout.pagePadding}>
                <Box display="flex" gap="10px" alignItems="center" justifyContent="space-between">
                    <Typography variant='h6' component="p" fontSize="14px">{title}</Typography>
                    <Box display="flex" gap="5px" alignItems="center">
                        <Typography flexShrink={0} noWrap fontSize={"12px"} color="GrayText">{xAxisLabel} </Typography>
                        {seriesConfig.map( sc => (
                            <Typography flexShrink={0} noWrap key={sc.key} fontWeight="bold" fontSize={"12px"} sx={{ color: sc.color }}> / {sc.label} </Typography>
                        ) )}
                    </Box>
                </Box>
                {finalOptions.showSeriesVisibilityToggleOptions && (
                    <Box display="flex" gap="10px" alignItems="center">
                        {seriesConfig.map( s => (
                            <FormControlLabel key={s.key} control={
                                <Checkbox size="small" checked={visibility[s.key]}
                                    onChange={() => setVisibility( p => ( { ...p, [s.key]: !p[s.key] } ) )}
                                    sx={{ color: s.color, '&.Mui-checked': { color: s.color } }} />
                            } label={<Typography fontSize="12px">{s.label}</Typography>} />
                        ) )}
                    </Box>
                )}
            </Box>

            <Box ref={containerRef} flexGrow={1} paddingBottom="1px" display="flex">
                {dimensions && (
                    <MultiBarPlotter
                        options={options}
                        xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel}
                        width={dimensions.width} height={dimensions.height}
                        data={data} seriesConfig={seriesConfig} activeSeries={activeSeries}
                        xAxisKey={xAxisKey} showDetailsDialog={showDetailsDialog}
                        hideDetailsDialog={() => setSelectedData( null )}
                    />
                )}
            </Box>

            {finalOptions.showLegend && (
                <Box justifyContent="center" display="flex" paddingTop="1px" marginBottom="20px" gap="20px" alignItems="center">
                    {seriesConfig.map( s => (
                        <Box key={s.key} display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                            <Typography fontSize="12px">{s.label}</Typography>
                        </Box>
                    ) )}
                </Box>
            )}

            <Popover autoFocus disableRestoreFocus open={Boolean( selectedData )} anchorReference="anchorPosition" anchorPosition={anchorPos} sx={{ pointerEvents: 'none' }}>
                <Box p={1.5}>
                    <Typography variant="subtitle2" fontWeight="bold">{selectedData?.[xAxisKey]}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {selectedData?._activeLabel}: <strong>{selectedData?._activeVal}</strong>
                    </Typography>
                </Box>
            </Popover>
        </Box>
    )
}

export default MultiBarGraph